import React from 'react';
import { ANIMATIONS } from 'shared/design-system/settings/variables/Animation';
import styled, { keyframes } from 'styled-components';
import { TrueImpactChild } from '../components/TrueImpactBadge';

interface TrueImpactAnimationProps {
  variant?: 'appear' | 'rotate' | 'pulse';
  className?: string;
  animationDelay?: number;
  animationDuration?: string;
  animationIterationCount?: string;
  animationDirection?: string;
  animationFillMode?: string;
  children?: TrueImpactChild;
  isFlex?: boolean;
  onClick?: () => unknown;
}

const BaseAnimations = styled.div<{
  animationDelay: TrueImpactAnimationProps['animationDelay'];
  animationDuration: TrueImpactAnimationProps['animationDuration'];
  animationIterationCount: TrueImpactAnimationProps['animationIterationCount'];
  animationDirection: TrueImpactAnimationProps['animationDirection'];
  animationFillMode: TrueImpactAnimationProps['animationFillMode'];
  isFlex: boolean;
}>`
  @keyframes rotate {
    0% {
      opacity: 0;
      transform: translateY(10%);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation-delay: ${props => (props.animationDelay || 0) * 150}ms;
  animation-duration: ${props => props.animationDuration}ms;
  animation-iteration-count: ${props => props.animationIterationCount};
  animation-direction: ${props => props.animationDirection};
  animation-fill-mode: ${props => props.animationFillMode};
  animation-timing-function: ${ANIMATIONS.easing};
  ${({ isFlex }) =>
    isFlex &&
    `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  `}
`;

export const appearKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10%);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Appear = styled(BaseAnimations)`
  opacity: 0;
  transform: translateY(10%);
  animation-name: ${appearKeyframes};
`;

const pulseKeyframes = keyframes`
0% {
  opacity: 0;
  transform: translateY(10%);
}
100% {
  opacity: 1;
  transform: translateY(0);
}
`;

const Pulse = styled(BaseAnimations)`
  opacity: 0;
  transform: translateY(10%);
  animation-name: ${pulseKeyframes};
`;

const rotateKeyframes = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const Rotate = styled(BaseAnimations)`
  transform: rotate(0deg);
  animation-name: ${rotateKeyframes};
`;

const TrueImpactAnimation: React.FC<TrueImpactAnimationProps> = ({
  variant = 'appear',
  className,
  animationDelay = 1,
  animationDuration = '150',
  animationIterationCount = '1',
  animationDirection = 'normal',
  animationFillMode = 'forwards',
  onClick,
  children,
  isFlex = true,
}) => {
  switch (variant) {
    case 'appear':
      return (
        <Appear
          isFlex={isFlex}
          className={className}
          onClick={onClick}
          animationDelay={animationDelay}
          animationDuration={animationDuration}
          animationIterationCount={animationIterationCount}
          animationDirection={animationDirection}
          animationFillMode={animationFillMode}
        >
          {children}
        </Appear>
      );
    case 'rotate':
      return (
        <Rotate
          isFlex={isFlex}
          className={className}
          onClick={onClick}
          animationDelay={animationDelay}
          animationDuration={animationDuration}
          animationIterationCount={animationIterationCount}
          animationDirection={animationDirection}
          animationFillMode={animationFillMode}
        >
          {children}
        </Rotate>
      );
    case 'pulse':
      return (
        <Pulse
          isFlex={isFlex}
          className={className}
          onClick={onClick}
          animationDelay={animationDelay}
          animationDuration={animationDuration}
          animationIterationCount={animationIterationCount}
          animationDirection={animationDirection}
          animationFillMode={animationFillMode}
        >
          {children}
        </Pulse>
      );
  }
};

export default TrueImpactAnimation;
